import {
  CHOOSE_TEAMS_INPUTS,
  FIELD_GOAL_INPUTS,
  KICKOFF_INPUTS,
  PUNT_INPUTS,
  REF_EVENT_INPUTS,
  REGULAR_PLAY_INPUTS,
  TEventTypes,
  TPlayDesigns,
  USER_ROLE_INPUTS,
} from '@/constants';
import { useUserStore } from '@/stores/user';
import {
  IMachineContext,
  ITwoRowGenericSelectorInp,
  TKickoffEvents,
  TMachineEvents,
  TMetaFuncInput,
  TPlays,
  TRefEventsActions,
  TRegularPlayEvents,
  TTeam,
  TTeamSide,
  TUserRole,
  TWorkflowType,
} from '@/types';
import { getUrlSearchParam, refEventValue, setUrlSearchParam } from '@/utilities';
import { determineResumePoint } from './utils';

export const onGameSelected = async ({ send, payload, generalState }: TMetaFuncInput) => {
  await generalState.selectGame(payload.gameId);

  const userState = useUserStore();
  if (userState.getUserRole() === TUserRole.CLOCK) {
    await generalState.setInitialState();
    generalState.startContinuousUpdates();
    return;
  }

  send(TMachineEvents.GAME_SELECTED);
};

export const onPlayerPreviewOk = async ({ send, generalState, playFlowState, context }: TMetaFuncInput) => {
  const { teams } = generalState;
  await generalState.setInitialState();
  generalState.startContinuousUpdates();

  const homeSide = getUrlSearchParam('homeSide');
  const userRole = getUrlSearchParam('role');
  const userState = useUserStore();

  if (!userRole || playFlowState.plays.length === 0) {
    send(TMachineEvents.GAME_SELECTED, {
      nextStateInput: USER_ROLE_INPUTS,
    });
  } else if (!homeSide) {
    send(TMachineEvents.CHOOSE_SIDES, {
      nextStateInput: CHOOSE_TEAMS_INPUTS(teams.home, teams.away),
    });
  } else {
    determineResumePoint(playFlowState.lastValidRealPlay, send, playFlowState, userState, context);
  }
};

export const onRoleSelected = async ({
  send,
  payload,
  generalState,
  playFlowState,
  context,
}: TMetaFuncInput) => {
  const userState = useUserStore();
  userState.setUserRole(payload.value);
  setUrlSearchParam('role', payload.value);

  if (
    payload.value === TUserRole.CLOCK ||
    payload.value === TUserRole.PLAYS ||
    payload.value === TUserRole.ALL
  ) {
    generalState.startHeartbeat();
  }

  const { teams } = generalState;

  const homeSide = getUrlSearchParam('homeSide');

  if (playFlowState.plays.length === 0) {
    send(TMachineEvents.ROLE_SELECTED, {
      nextStateInput: CHOOSE_TEAMS_INPUTS(teams.home, teams.away),
      ctxUpdate: {
        currentWorkflow: TWorkflowType.COIN,
      } as IMachineContext,
    });
  } else if (!homeSide) {
    send(TMachineEvents.CHOOSE_SIDES, {
      nextStateInput: CHOOSE_TEAMS_INPUTS(teams.home, teams.away),
    });
  } else {
    determineResumePoint(playFlowState.lastValidRealPlay, send, playFlowState, userState, context);
  }
};

export const onSidesSelected = ({ send, payload, playFlowState, context }: TMetaFuncInput) => {
  const userState = useUserStore();
  const lastValidPlay = playFlowState.lastValidRealPlay;

  playFlowState.setTeamSides(payload.value as TTeam, payload.value === TTeam.HOME ? TTeam.AWAY : TTeam.HOME);

  if (
    (payload.value === TTeam.HOME && lastValidPlay.resultingIsHomeTeamSideOfField) ||
    (payload.value === TTeam.AWAY && !lastValidPlay.resultingIsHomeTeamSideOfField)
  ) {
    playFlowState.setYardline(lastValidPlay.resultingYardLine, TTeamSide.LEFT);
  } else {
    playFlowState.setYardline(lastValidPlay.resultingYardLine, TTeamSide.RIGHT);
  }

  playFlowState.setDriveDirection(
    playFlowState.activeTeam === playFlowState.teams.right ? TTeamSide.LEFT : TTeamSide.RIGHT
  );

  determineResumePoint(lastValidPlay, send, playFlowState, userState, context);
};

export const onFormationSelected = async ({ send, payload, generalState, playFlowState }: TMetaFuncInput) => {
  switch (payload.value) {
    case TPlays.REGULAR:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.OFFENSE_COMING_ON,
          value: true,
        },
        generalState.currentGame.gameId
      );

      send(TMachineEvents.REGULAR_PLAY_SELECTED, {
        ctxUpdate: {
          currentWorkflow: TWorkflowType.REGULAR_PLAY,
          currentPlay: {},
        },
        nextStateInput: REGULAR_PLAY_INPUTS(TRegularPlayEvents.BACK_TO_IDEL),
      });
      break;
    case TPlays.FG:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.PLAY_DESIGN,
          value: TPlayDesigns.FG,
        },
        generalState.currentGame.gameId
      );

      send(TMachineEvents.FIELD_GOAL_SELECTED, {
        ctxUpdate: {
          currentWorkflow: TWorkflowType.FIELD_GOAL,
          currentPlay: {},
        },
        nextStateInput: FIELD_GOAL_INPUTS,
      });
      break;
    case TPlays.KICKOFF:
      send(TMachineEvents.KICKOFF_SELECTED, {
        ctxUpdate: {
          currentWorkflow: TWorkflowType.KICKOFF,
          currentPlay: {},
        },
        nextStateInput: KICKOFF_INPUTS(TKickoffEvents.TO_IDEL),
      });
      break;
    case TPlays.PUNT:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.PLAY_DESIGN,
          value: TPlayDesigns.PUNT,
        },
        generalState.currentGame.gameId
      );

      send(TMachineEvents.TO_PUNT, {
        ctxUpdate: {
          currentWorkflow: TWorkflowType.PUNT,
          currentPlay: {},
        },
        nextStateInput: PUNT_INPUTS,
      });
      break;
    case TPlays.REF:
    default:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.REF_EVENT,
          value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
        },
        generalState.currentGame.gameId
      );

      send(TMachineEvents.ON_REF_EVENT, {
        nextStateInput: {
          ...REF_EVENT_INPUTS,
          backTo: TRefEventsActions.BACK_TO_IDLE,
        } as ITwoRowGenericSelectorInp,
        ctxUpdate: {
          currentWorkflow: null,
          currentPlay: {},
        },
      });
      break;
  }
};
